import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  Package,
  Paperclip,
  Send,
  Trash2,
} from "lucide-react";
import {
  Badge,
  Button,
  Input,
  OverflowMenu,
  type OverflowMenuItem,
  StatusStepper,
  buttonVariants,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { BrokerRecordLink } from "@/components/crosslinks/BrokerCrossLinks";
import { VendorRecordLink } from "@/components/crosslinks/VendorCrossLinks";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { isPastCalendarDay, useDateFormatter } from "@/utils/datetime";
import {
  portalAccountsApi,
  type PortalAccountActivityDto,
  type PortalAccountAttachmentDto,
  type PortalAccountDetailDto,
  type TemporaryPasswordResult,
  type UpdatePortalAccountIdentityPayload,
} from "@/api/portalAccounts";
import { AccountStatusModal } from "./AccountStatusModal";
import type {
  CollectionPackageQueueItem,
  CollectionPackageStatus,
} from "@/api/collectionPackages";
import {
  collectionStagingApi,
  type StagedSubmissionListItem,
} from "@/api/collectionStaging";
import {
  OwnerPicker,
  type OwnerSelection,
} from "@/components/Collection/OwnerPicker";
import { InternalUserPicker } from "@/components/users/InternalUserPicker";
import { firstCompatibleOwner } from "@/components/Collection/portalAccountOwner";
import { PACKAGE_STATUS } from "@/components/Collection/collectionStatusLabels";
import { fallbackOwnerLabel, useOwnerLabels } from "@/api/ownerLabels";
import { formatCalendarDate } from "@/utils/datetime";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import { registryKeys } from "@/hooks/portfolioAdmin/useRegistries";
// Attach-record expansion (WS6) — External Account (PortalUser) gains
// Messages + Tasks tabs alongside the package-history surfaces.
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { PortalAccountOverviewTab } from "@/components/portal/PortalAccountOverviewTab";

function buildComposerHref(account: PortalAccountDetailDto): string {
  const qs = new URLSearchParams({
    accountType: account.accountType,
    email: account.email,
    fullName: account.fullName,
  });
  if (account.linkedRecipientId) {
    qs.set("recipientId", account.linkedRecipientId);
  }
  // Default the Owner to the first compatible attachment, if any. The
  // account-first composer (Phase 3) also re-derives this from the
  // account's attachments at mount, but passing it on the URL keeps the
  // pre-fill visible before the account fetch settles.
  const owner = firstCompatibleOwner(account.attachments);
  if (owner) {
    qs.set("ownerEntityType", owner.ownerEntityType);
    qs.set("ownerEntityId", owner.ownerEntityId);
    qs.set("ownerDisplayName", owner.ownerDisplayName);
  }
  return `/collection/packages/new?${qs.toString()}`;
}

// Review loop — label and tone from the one staff label map; the raw
// CollectionPackageStatus code ("PartiallyApproved") never renders.
function packageStatusBadge(status: CollectionPackageStatus) {
  const s = PACKAGE_STATUS[status];
  return <Badge status={s?.tone ?? "draft"}>{s?.label ?? "—"}</Badge>;
}

/**
 * `CollectionPackage.DueDate` is a WALL-CALENDAR day — the write contract is
 * `UpdateCollectionDueDateRequest(DateOnly? DueDate)`, widened server-side —
 * so comparing it as an INSTANT painted a package due TODAY red for the last
 * seven hours of the day it was actually due.
 *
 * It is also stored on two conventions (midnight UTC from the staff editor,
 * noon UTC from the create paths' `calendarDateInputToIso`), which went wrong
 * in opposite directions under the instant comparison. `isPastCalendarDay` is
 * time-of-day agnostic on the due side, so one rule covers both.
 */
function isOverdue(due: string | null): boolean {
  return isPastCalendarDay(due);
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Disable / Re-enable / Archive — copy for the four status actions.
type StatusAction = "disable" | "enable" | "archive" | "unarchive";

const STATUS_SUCCESS: Record<StatusAction, string> = {
  disable: "Account disabled.",
  enable: "Account re-enabled.",
  archive: "Account archived.",
  unarchive: "Account unarchived.",
};

const STATUS_MODAL: Record<
  StatusAction,
  {
    title: string;
    description: string;
    label: string;
    placeholder: string;
    reasonRequired: boolean;
    confirmLabel: string;
    destructive: boolean;
  }
> = {
  disable: {
    title: "Disable account?",
    description:
      "The user will be signed out of the portal and blocked from logging in. In-flight packages are frozen, not cancelled.",
    label: "Reason for disabling",
    placeholder: "Explain why this account is being disabled. Min. 10 characters.",
    reasonRequired: true,
    confirmLabel: "DISABLE ACCOUNT",
    destructive: true,
  },
  enable: {
    title: "Re-enable account?",
    description: "The account returns to normal; in-flight packages resume as before.",
    label: "Reason for re-enabling",
    placeholder: "Explain why this account is being re-enabled. Min. 10 characters.",
    reasonRequired: true,
    confirmLabel: "RE-ENABLE",
    destructive: false,
  },
  archive: {
    title: "Archive account?",
    description: "Hides the account from the default list. It can be unarchived at any time.",
    label: "Reason",
    placeholder: "Optional note.",
    reasonRequired: false,
    confirmLabel: "ARCHIVE",
    destructive: false,
  },
  unarchive: {
    title: "Unarchive account?",
    description: "Returns the account to the default list.",
    label: "Reason",
    placeholder: "Optional note.",
    reasonRequired: false,
    confirmLabel: "UNARCHIVE",
    destructive: false,
  },
};

// Disable / Re-enable / Archive — humanise the AuditAction string for the
// activity log (StatusChange covers both disable + re-enable; the description
// carries the actual verb + reason).
function activityActionLabel(action: string): string {
  switch (action) {
    case "StatusChange":
      return "Status change";
    case "Archive":
      return "Archived";
    case "Unarchive":
      return "Unarchived";
    default:
      return action;
  }
}

function ActivityLogSection({
  rows,
  loading,
  formatDate,
}: {
  rows: PortalAccountActivityDto[];
  loading: boolean;
  formatDate: (iso: string) => string;
}) {
  return (
    <section className="flex flex-col gap-space-3" data-testid="portal-account-activity-log">
      <h2 className="text-h5 font-semibold">Activity log</h2>
      {loading ? (
        <div className="text-small text-gray-mid">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-small text-gray-mid">No status changes yet.</div>
      ) : (
        <ul className="flex flex-col gap-space-2">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex flex-col gap-space-1 rounded border border-border-light p-space-3"
            >
              <div className="flex items-center justify-between gap-space-3">
                <span className="text-small font-semibold text-onyx">
                  {activityActionLabel(r.action)}
                </span>
                <span className="text-caption text-gray-mid">{formatDate(r.occurredAtUtc)}</span>
              </div>
              <div className="text-small text-onyx break-words">{r.description}</div>
              <div className="text-caption text-gray-mid">
                {r.actorDisplayName ?? "Unknown user"}
                {r.actorRoleCode ? ` · ${r.actorRoleCode}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

type TabKey = "overview" | "packages" | "completed" | "messages" | "tasks" | "calendar";

const TAB_KEYS: readonly TabKey[] = [
  "overview",
  "packages",
  "completed",
  "messages",
  "tasks",
  "calendar",
] as const;

function parseTab(raw: string | null): TabKey {
  return TAB_KEYS.includes(raw as TabKey) ? (raw as TabKey) : "overview";
}

// External Intake & Collection — Remediation Tier 2 #9 (Prompt 9-i).
// Single-account detail page. Review-surface consolidation §1 removed the
// inline "Submissions needing review" tab: the canonical review surface is
// the Review Queue slide-over. This page now leads with an Overview tab
// (account summary + recent activity rail), then keeps the account's
// package history ("All packages" + "Completed" tabs) and the shared
// Messages / Tasks tabs.

export function PortalAccountDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const { date } = useDateFormatter();
  const [params, setParams] = useSearchParams();

  const tab: TabKey = parseTab(params.get("tab"));

  const setTab = (next: TabKey) => {
    const merged = new URLSearchParams(params);
    if (next === "overview") merged.delete("tab");
    else merged.set("tab", next);
    setParams(merged, { replace: true });
  };

  const accountQuery = useQuery({
    queryKey: ["portal-account", id],
    queryFn: () => portalAccountsApi.get(id),
    enabled: Boolean(id),
  });
  useBreadcrumbs([
    { label: "External Collection" },
    { label: "Portal Accounts", href: "/admin/portal-accounts" },
    { label: accountQuery.data?.fullName ?? "Account" },
  ]);

  // Account-First — urgency summary for the header badge.
  const summaryQuery = useQuery({
    queryKey: ["portal-account-summary", id],
    queryFn: () => portalAccountsApi.submissionSummary(id),
    enabled: Boolean(id),
  });

  const [picked, setPicked] = useState<OwnerSelection | null>(null);
  const [notes, setNotes] = useState("");
  // Account-First — the All-packages tab shows every status by default now
  // that outstanding work lives on the review tab.
  const [showAllPackages, setShowAllPackages] = useState(true);
  const [attachOpen, setAttachOpen] = useState(false);
  // Full activity log is rendered below the tab content on the Overview
  // tab. The rail's "See all activity →" link scrolls it into view.
  const activityLogRef = useRef<HTMLDivElement>(null);
  const scrollToActivityLog = () => {
    activityLogRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // §1 — the "Completed" tab still reuses the staging-list endpoint,
  // narrowed to this account's approved submissions.
  const completedQuery = useQuery({
    queryKey: ["portal-account-completed", id],
    queryFn: () =>
      collectionStagingApi.list({ portalUserId: id, reviewStatus: "Approved" }),
    enabled: Boolean(id),
  });

  const packagesQuery = useQuery({
    queryKey: ["portal-account-packages", id, showAllPackages],
    queryFn: () => portalAccountsApi.listPackages(id, showAllPackages),
    enabled: Boolean(id),
  });

  // PROB-04 — resolve owner GUIDs for the document-requests table OWNER
  // column AND the Attach Owners modal's "Attached records" list. One
  // batched request services both surfaces.
  const ownerRefs = [
    ...(packagesQuery.data ?? []).map((p) => ({
      type: p.ownerEntityType,
      id: p.ownerEntityId,
    })),
    ...(accountQuery.data?.attachments ?? []).map((a) => ({
      type: a.entityType,
      id: a.entityId,
    })),
  ];
  const owners = useOwnerLabels(ownerRefs);

  const completedRows = completedQuery.data?.items ?? [];

  const packageColumns: Column<CollectionPackageQueueItem>[] = [
    {
      key: "id",
      header: "Package",
      render: (p) => (
        <Link
          to={`/collection/packages/${p.id}?from=${id}`}
          className="font-semibold text-onyx hover:text-gold-shadow"
        >
          #{p.id.slice(0, 8)}
        </Link>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      render: (p) => {
        const ref = { type: p.ownerEntityType, id: p.ownerEntityId };
        const lbl = owners.lookup(ref);
        return (
          <span className="text-small text-onyx">
            <span className="text-caption uppercase text-gray-mid">
              {p.ownerEntityType === "LegalEntity" ? "Entity" : p.ownerEntityType}
            </span>{" "}
            {lbl ? lbl.label : fallbackOwnerLabel(ref)}
          </span>
        );
      },
    },
    {
      key: "role",
      header: "Role",
      render: (p) =>
        p.recipientRoleLabel ? (
          <span className="text-small text-onyx">{p.recipientRoleLabel}</span>
        ) : (
          <span className="text-caption text-gray-mid italic">—</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (p) => packageStatusBadge(p.status),
    },
    {
      key: "progress",
      header: "Progress",
      render: (p) => (
        <span className="text-small text-onyx">
          {p.itemsCompleted} / {p.itemsTotal}
        </span>
      ),
    },
    {
      key: "due",
      header: "Due",
      render: (p) =>
        p.dueDate ? (
          <span
            className={
              isOverdue(p.dueDate)
                ? "text-small text-red-600 font-semibold"
                : "text-small text-onyx"
            }
          >
            {formatCalendarDate(p.dueDate)}
          </span>
        ) : (
          <span className="text-caption text-gray-mid italic">—</span>
        ),
    },
  ];

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["portal-account", id] });
    void qc.invalidateQueries({ queryKey: ["portal-accounts"] });
  };

  // Stale-after-save sweep — attaching/detaching a Broker or Vendor record
  // also changes what that record's own detail page shows (portal-account
  // cross-links), so refresh the linked record's detail cache too.
  const invalidateLinkedRecord = (entityType: string, entityId: string) => {
    if (entityType === "Broker") {
      void qc.invalidateQueries({ queryKey: brokerKeys.detail(entityId) });
    } else if (entityType === "Vendor") {
      void qc.invalidateQueries({ queryKey: registryKeys.vendor(entityId) });
    }
  };

  const attachMut = useMutation({
    mutationFn: async () => {
      if (!picked)
        throw new Error("Pick an internal record before attaching.");
      const entityType =
        picked.ownerEntityType === "Entity"
          ? "LegalEntity"
          : picked.ownerEntityType;
      return portalAccountsApi.attach(id, {
        entityType,
        entityId: picked.ownerEntityId,
        notes: notes.trim() || null,
      });
    },
    onSuccess: () => {
      toast.success(`Attached ${picked?.ownerDisplayName ?? "record"}.`);
      if (picked) {
        invalidateLinkedRecord(picked.ownerEntityType, picked.ownerEntityId);
      }
      setPicked(null);
      setNotes("");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  const detachMut = useMutation({
    mutationFn: (attachmentId: string) =>
      portalAccountsApi.detach(id, attachmentId),
    onSuccess: (_data, attachmentId) => {
      toast.success("Detached.");
      // The attachment row is still in the cached account detail here —
      // resolve the linked record's type/id from it before invalidating.
      const detached = accountQuery.data?.attachments.find(
        (a) => a.id === attachmentId,
      );
      if (detached) {
        invalidateLinkedRecord(detached.entityType, detached.entityId);
      }
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  // PROB-H — staff assignees on this account. Live editor: each add / remove
  // writes immediately, then refreshes the account header + the registry
  // list so the ASSIGNED TO column stays in sync. Account assignment changes
  // less often than queue assignment, so this lives in a quiet section near
  // the top rather than on every list row.
  const assigneesQuery = useQuery({
    queryKey: ["portal-account-assignees", id],
    queryFn: () => portalAccountsApi.listAssignees(id),
    enabled: Boolean(id),
  });
  const assigneeIds = (assigneesQuery.data ?? []).map((a) => a.userId);

  const refreshAssignees = () => {
    void qc.invalidateQueries({ queryKey: ["portal-account-assignees", id] });
    invalidate();
  };
  const addAssigneeMut = useMutation({
    mutationFn: (userId: string) => portalAccountsApi.addAssignee(id, userId),
    onSuccess: refreshAssignees,
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });
  const removeAssigneeMut = useMutation({
    mutationFn: (userId: string) => portalAccountsApi.removeAssignee(id, userId),
    onSuccess: refreshAssignees,
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });
  const handleAssigneesChange = (next: string[]) => {
    for (const uid of next) {
      if (!assigneeIds.includes(uid)) addAssigneeMut.mutate(uid);
    }
    for (const uid of assigneeIds) {
      if (!next.includes(uid)) removeAssigneeMut.mutate(uid);
    }
  };

  // Disable / Re-enable / Archive — activity log + status actions.
  const activityQuery = useQuery({
    queryKey: ["portal-account-activity", id],
    queryFn: () => portalAccountsApi.getActivity(id),
    enabled: Boolean(id),
  });

  const [statusAction, setStatusAction] =
    useState<null | "disable" | "enable" | "archive" | "unarchive">(null);

  // Shared by the status actions and the identity edit: both mutate the
  // account row and append to its activity log, so refresh the detail, the
  // activity feed, and the registry list together.
  const invalidateAccountScope = () => {
    void qc.invalidateQueries({ queryKey: ["portal-account", id] });
    void qc.invalidateQueries({ queryKey: ["portal-account-activity", id] });
    void qc.invalidateQueries({ queryKey: ["portal-accounts"] });
  };

  const statusMut = useMutation({
    mutationFn: (vars: {
      action: "disable" | "enable" | "archive" | "unarchive";
      reason: string;
    }) => {
      switch (vars.action) {
        case "disable":
          return portalAccountsApi.disable(id, vars.reason);
        case "enable":
          return portalAccountsApi.enable(id, vars.reason);
        case "archive":
          return portalAccountsApi.archive(id, vars.reason || undefined);
        case "unarchive":
          return portalAccountsApi.unarchive(id, vars.reason || undefined);
      }
    },
    onSuccess: (_data, vars) => {
      toast.success(STATUS_SUCCESS[vars.action]);
      setStatusAction(null);
      invalidateAccountScope();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  // UAT F3 — staff edit of the account's display name / login email.
  const [editOpen, setEditOpen] = useState(false);
  const editMut = useMutation({
    mutationFn: (body: UpdatePortalAccountIdentityPayload) =>
      portalAccountsApi.updateIdentity(id, body),
    onSuccess: () => {
      toast.success("Account updated.");
      setEditOpen(false);
      invalidateAccountScope();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  // WS2 — credential actions. Neither changes the account row itself; each
  // just appends an audit row, so refresh the activity feed via the shared
  // scope helper the status/edit mutations already use.
  const resendLinkMut = useMutation({
    mutationFn: () => portalAccountsApi.resendPasswordLink(id),
    onSuccess: (data) => {
      if (data.delivered) toast.success(data.message);
      else toast.error(data.message);
      invalidateAccountScope();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  // WS2 — set temporary password. Mounted on demand; the result is rendered
  // inside the dialog (shown once) rather than toasted, so onSuccess only
  // refreshes the activity feed.
  const [tempPasswordOpen, setTempPasswordOpen] = useState(false);
  const tempPasswordMut = useMutation({
    mutationFn: () => portalAccountsApi.setTemporaryPassword(id),
    onSuccess: () => {
      invalidateAccountScope();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  if (accountQuery.isLoading) {
    return (
      <div className="py-space-6 text-center text-small text-gray-mid">
        Loading…
      </div>
    );
  }

  const account = accountQuery.data;
  if (!account) {
    return (
      <div className="flex flex-col gap-space-3">
        <Link to="/admin/portal-accounts" className="text-small text-gray-mid">
          <ArrowLeft size={14} aria-hidden /> Back to Portal Accounts
        </Link>
        <div className="text-default">Account not found.</div>
      </div>
    );
  }

  const summary = summaryQuery.data;

  // Lifecycle stepper — Invited → Active. emailConfirmed is the active
  // signal (firstLoginAt isn't on the DTO today).
  const lifecycleComplete = account.emailConfirmed === true;
  const lifecycleCurrentIndex = lifecycleComplete ? 1 : 0;

  // Destructive actions (Disable/Enable + Archive/Unarchive) live behind
  // the "More actions" overflow per Layout Standard §3 — keep the primary
  // actions inline. The non-destructive identity edit (UAT F3) leads.
  const overflowItems: OverflowMenuItem[] = [
    {
      label: "Edit account",
      onSelect: () => setEditOpen(true),
    },
    // WS2 — credential actions. Omitted for disabled/archived accounts: the
    // user can't sign in, so a fresh link / temp password can't be used (the
    // backend 409s anyway) — don't offer an action that will obviously fail.
    ...(!account.isDisabled && !account.isArchived
      ? [
          {
            label: "Resend password link",
            confirm: "Send a fresh password link to this account?",
            onSelect: () => resendLinkMut.mutate(),
          },
          {
            label: "Set temporary password",
            onSelect: () => setTempPasswordOpen(true),
          },
        ]
      : []),
    account.isDisabled
      ? {
          label: "Re-enable account",
          onSelect: () => setStatusAction("enable"),
        }
      : {
          label: "Disable account",
          destructive: true,
          onSelect: () => setStatusAction("disable"),
        },
    account.isArchived
      ? {
          label: "Unarchive account",
          onSelect: () => setStatusAction("unarchive"),
        }
      : {
          label: "Archive account",
          onSelect: () => setStatusAction("archive"),
        },
  ];

  // Tabs (Layout Standard §3) — Overview leads, packages history follows,
  // shared Messages + Tasks tabs close out the strip.
  const tabItems: ReadonlyArray<EntityTabItem<TabKey>> = [
    { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
    {
      key: "packages",
      label: "All packages",
      icon: Package,
      badge: packagesQuery.data?.length ?? null,
    },
    {
      key: "completed",
      label: "Completed",
      icon: CheckCircle2,
      badge: completedRows.length || null,
    },
    { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
    { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
    { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Portal Accounts"
        title={account.fullName}
        description={account.email}
        actions={
          <>
            {/* Broker ↔ External User cross-link — a broker portal account
                links back to its CRM Broker record. */}
            {account.kind === "Broker" &&
              (account.linkedBrokerId ? (
                <BrokerRecordLink
                  brokerId={account.linkedBrokerId}
                  className={buttonVariants({ variant: "secondary", size: "sm" })}
                />
              ) : (
                <span className="text-small text-gray-mid italic">No broker record linked</span>
              ))}
            {/* Vendor ↔ External User cross-link — a vendor portal account
                links back to its PortfolioAdmin Vendor record. */}
            {account.accountType === "Vendor" &&
              (account.linkedVendorId ? (
                <VendorRecordLink
                  vendorId={account.linkedVendorId}
                  label={
                    account.linkedVendorName
                      ? `View vendor record (${account.linkedVendorName})`
                      : undefined
                  }
                  className={buttonVariants({ variant: "secondary", size: "sm" })}
                />
              ) : (
                <span className="text-small text-gray-mid italic">No vendor record linked</span>
              ))}
            <Link to={buildComposerHref(account)}>
              <Button size="sm" variant="secondary">
                <Send size={14} strokeWidth={1.5} aria-hidden /> Send another request
              </Button>
            </Link>
            <Button size="sm" type="button" onClick={() => setAttachOpen(true)}>
              <Paperclip size={14} strokeWidth={1.5} aria-hidden /> Attach Owners
            </Button>
            {/* Destructive actions (Disable / Re-enable / Archive / Unarchive)
                live in the "More actions" cluster per Layout Standard §3. */}
            <OverflowMenu
              label="More actions"
              data-testid="portal-account-more-actions"
              items={overflowItems}
            />
          </>
        }
      />

      {/* Lifecycle stepper (Invited → Active) + terminal-state badges. */}
      <div className="flex flex-wrap items-center gap-space-3">
        <StatusStepper
          steps={[
            {
              key: "invited",
              label: "Invited",
              hint: "Account has been invited but has not confirmed their email.",
            },
            {
              key: "active",
              label: "Active",
              hint: "Account has confirmed their email and can access the portal.",
            },
          ]}
          currentIndex={lifecycleCurrentIndex}
          complete={lifecycleComplete}
          label="Portal account lifecycle"
          data-testid="portal-account-lifecycle-stepper"
        />
        {/* Disable / Archive — terminal override chips. Archived takes
            precedence visually (it's the higher-order terminal state). */}
        {account.isArchived ? (
          <Badge status="archived" data-testid="portal-account-archived-badge">
            Archived
          </Badge>
        ) : null}
        {account.isDisabled ? (
          <Badge status="error" data-testid="portal-account-disabled-badge">
            Disabled
          </Badge>
        ) : null}
      </div>

      {/* Attached-record count + staff assignee control. Both live on their
          own row beneath the header, separate from the urgency pills. */}
      <div className="flex flex-wrap items-center gap-space-3">
        <span className="text-small text-gray-mid">
          {account.attachments.length} attached record
          {account.attachments.length === 1 ? "" : "s"}
        </span>
        {/* PROB-H — staff assignees on this account. Live editor: each
            add/remove writes immediately and refreshes the header + registry. */}
        <div className="flex items-center gap-space-2">
          <span className="text-small text-gray-mid">Assigned to:</span>
          <div className="w-72">
            <InternalUserPicker
              value={assigneeIds}
              onChange={handleAssigneesChange}
              placeholder="Assign staff…"
            />
          </div>
        </div>
      </div>

      {/* Account-First — urgency pills beneath the header. */}
      {summary && (summary.packageCount > 0 || summary.totalPendingCount > 0) && (
        <div className="flex flex-wrap items-center gap-space-3">
          {summary.packageCount > 0 ? (
            <>
              <Badge status="info">
                {summary.packageCount} package{summary.packageCount === 1 ? "" : "s"}
              </Badge>
              <Badge status="archived">
                {summary.totalItemCount} item{summary.totalItemCount === 1 ? "" : "s"}
              </Badge>
              {summary.pendingRecipientActionCount > 0 && (
                <Badge status="review">
                  {summary.pendingRecipientActionCount} pending recipient action
                </Badge>
              )}
            </>
          ) : null}
          {summary.totalPendingCount > 0 ? (
            summary.overdueCount > 0 ? (
              <Badge status="error">{summary.overdueCount} overdue</Badge>
            ) : (
              <Badge status="review">
                {summary.awaitingReviewCount} awaiting review
              </Badge>
            )
          ) : null}
        </div>
      )}

      {/* §1 — read-only hand-off to the canonical review surface. The inline
          review tab was removed; this line links to the Review Queue
          pre-filtered to this account so the reviewer always sees the file
          before approving / requesting changes. */}
      {summary && summary.awaitingReviewCount > 0 && (
        <div className="flex items-center gap-space-2 rounded-md border border-border-light bg-white-warm p-space-3 text-small text-onyx">
          <span>
            {summary.awaitingReviewCount} submission
            {summary.awaitingReviewCount === 1 ? "" : "s"} awaiting review
          </span>
          <Link
            to={`/collection/staging?recipient=${encodeURIComponent(account.email)}`}
            className="inline-flex items-center font-semibold text-onyx hover:text-gold-shadow"
          >
            open in Review Queue
            <ArrowRight size={14} className="ml-1 inline" aria-hidden />
          </Link>
        </div>
      )}

      {/* Layout Standard §6.1 — sticky entity tab bar. Overview leads; the
          package-history tabs follow; Messages + Tasks close out. */}
      <EntityTabBar
        ariaLabel="Portal account sections"
        testIdPrefix="portal-account"
        items={tabItems}
        value={tab}
        onChange={setTab}
        showLabels
        className="mb-space-2"
      />

      {tab === "overview" && (
        <PortalAccountOverviewTab
          account={account}
          summary={summary}
          activity={activityQuery.data ?? []}
          activityLoading={activityQuery.isLoading}
          ownerLookup={owners.lookup}
          onEditAttachments={() => setAttachOpen(true)}
          onSeeAllActivity={scrollToActivityLog}
        />
      )}

      {tab === "packages" && (
        <section className="flex flex-col gap-space-3">
          <div className="flex items-center justify-between gap-space-3">
            <h2 className="text-h5 font-semibold">All packages</h2>
            <label className="inline-flex items-center gap-space-2 text-small text-gray-mid">
              <input
                type="checkbox"
                checked={showAllPackages}
                onChange={(e) => setShowAllPackages(e.target.checked)}
              />
              Show all (incl. completed &amp; cancelled)
            </label>
          </div>
          <DataTable
            rows={packagesQuery.data ?? []}
            columns={packageColumns}
            rowKey={(p) => p.id}
            loading={packagesQuery.isLoading}
            emptyMessage={
              showAllPackages
                ? "No document requests for this account."
                : "No outstanding requests. Toggle “Show all” to see history."
            }
          />
          {packagesQuery.error && (
            <div className="text-small text-status-error">
              {extractErrorMessage(packagesQuery.error)}
            </div>
          )}
        </section>
      )}

      {tab === "completed" && (
        <CompletedTab
          accountId={id}
          rows={completedRows}
          loading={completedQuery.isLoading}
          error={
            completedQuery.error ? extractErrorMessage(completedQuery.error) : null
          }
        />
      )}

      {tab === "messages" && (
        <RelatedConversationsPanel
          resourceType="portal_user"
          resourceId={id}
          entityLabel={account.fullName || account.email}
        />
      )}

      {tab === "tasks" && (
        <TaskListMini
          relatedEntityType="PortalUser"
          relatedEntityId={id}
          addButtonLabel="+ Add task"
        />
      )}
      {tab === "calendar" && (
        <AppointmentListMini
          relatedEntityType="PortalUser"
          relatedEntityId={id}
          entityLabel={account.fullName || account.email}
          entityArchived={account.isArchived}
        />
      )}

      <Modal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        dismissOnBackdropClick={false}
        title="Attach Owners"
        description="Link this account to internal Deal / Person / Entity / Business records. Attachments drive the filing-owner default and quick-picks when sending document requests."
        size="lg"
      >
        <div className="flex flex-col gap-space-4">
          <section className="flex flex-col gap-space-3">
            <h3 className="text-h5 font-semibold">Attached records</h3>
            {account.attachments.length === 0 ? (
              <div className="text-small text-gray-mid">
                No internal records attached yet.
              </div>
            ) : (
              <ul className="flex flex-col gap-space-2">
                {account.attachments.map((a: PortalAccountAttachmentDto) => {
                  const ref = { type: a.entityType, id: a.entityId };
                  const lbl = owners.lookup(ref);
                  const pretty = a.entityType === "LegalEntity" ? "Entity" : a.entityType;
                  return (
                  <li
                    key={a.id}
                    className="flex items-start justify-between gap-space-3 rounded border border-gray-light p-space-3"
                  >
                    <div>
                      <div className="text-default font-medium">
                        <span className="text-caption uppercase text-gray-mid mr-space-1">{pretty}</span>
                        {lbl ? lbl.label : fallbackOwnerLabel(ref)}
                      </div>
                      {lbl?.secondary && (
                        <div className="text-caption text-gray-mid">{lbl.secondary}</div>
                      )}
                      {a.notes && (
                        <div className="text-small text-gray-mid">{a.notes}</div>
                      )}
                      <div className="text-tiny text-gray-mid">
                        Attached {date(a.createdAt)}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="tertiary"
                      onClick={() => detachMut.mutate(a.id)}
                      disabled={detachMut.isPending}
                      aria-label={`Detach ${a.entityType}`}
                    >
                      <Trash2 size={14} aria-hidden />
                    </Button>
                  </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="flex flex-col gap-space-3 rounded border border-gray-light p-space-3">
            <h3 className="text-h5 font-semibold">Attach an internal record</h3>
            <OwnerPicker value={picked} onChange={setPicked} />
            <div>
              <label className="block text-small text-gray-mid">Note (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. primary contact"
              />
            </div>
            <div>
              <Button
                type="button"
                onClick={() => attachMut.mutate()}
                disabled={!picked || attachMut.isPending}
                loading={attachMut.isPending}
              >
                Attach
              </Button>
            </div>
          </section>
        </div>
      </Modal>

      {/* Disable / Re-enable / Archive — full activity log. The Overview
          rail shows a preview + a "See all activity →" link that scrolls
          this section into view. */}
      {tab === "overview" && (
        <div ref={activityLogRef}>
          <ActivityLogSection
            rows={activityQuery.data ?? []}
            loading={activityQuery.isLoading}
            formatDate={date}
          />
        </div>
      )}

      {statusAction && (
        <AccountStatusModal
          open
          {...STATUS_MODAL[statusAction]}
          pending={statusMut.isPending}
          onClose={() => setStatusAction(null)}
          onConfirm={(reason) => statusMut.mutate({ action: statusAction, reason })}
        />
      )}

      {/* UAT F3 — identity edit dialog. Mounted on demand so the form state
          re-seeds from the current account every time it opens. */}
      {editOpen && (
        <EditAccountDialog
          account={account}
          pending={editMut.isPending}
          onClose={() => setEditOpen(false)}
          onSubmit={(body) => editMut.mutate(body)}
        />
      )}

      {/* WS2 — set temporary password. Mounted on demand; the generated
          password is shown once inside the dialog and never persisted or
          logged. Resetting the mutation on close clears the shown value. */}
      {tempPasswordOpen && (
        <SetTemporaryPasswordDialog
          pending={tempPasswordMut.isPending}
          result={tempPasswordMut.data ?? null}
          onGenerate={() => tempPasswordMut.mutate()}
          onClose={() => {
            setTempPasswordOpen(false);
            tempPasswordMut.reset();
          }}
        />
      )}
    </div>
  );
}

// UAT F3 — inline identity edit dialog (mirrors the in-page Attach Owners
// modal rather than a separate file). Prefills the current name + login
// email; on open it fetches the advisory e-signature impact count so staff
// see that in-flight envelopes will NOT follow an email change.
function EditAccountDialog({
  account,
  pending,
  onClose,
  onSubmit,
}: {
  account: PortalAccountDetailDto;
  pending: boolean;
  onClose: () => void;
  onSubmit: (body: UpdatePortalAccountIdentityPayload) => void;
}) {
  const [fullName, setFullName] = useState(account.fullName);
  const [email, setEmail] = useState(account.email);
  const [envelopeCount, setEnvelopeCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    portalAccountsApi
      .identityImpact(account.id)
      .then((impact) => {
        if (!cancelled) setEnvelopeCount(impact.activeSignatureEnvelopeCount);
      })
      .catch(() => {
        // Advisory only — the dialog stays usable when the check fails.
      });
    return () => {
      cancelled = true;
    };
  }, [account.id]);

  const trimmedName = fullName.trim();
  const trimmedEmail = email.trim();
  const nameChanged = trimmedName !== account.fullName;
  const emailChanged = trimmedEmail.toLowerCase() !== account.email;
  const valid =
    trimmedName.length > 0 &&
    trimmedEmail.length > 0 &&
    (nameChanged || emailChanged);

  const submit = () => {
    if (!valid || pending) return;
    const body: UpdatePortalAccountIdentityPayload = {};
    if (nameChanged) body.fullName = trimmedName;
    if (emailChanged) body.email = trimmedEmail;
    onSubmit(body);
  };

  return (
    <Modal
      open
      onClose={pending ? () => {} : onClose}
      dismissOnBackdropClick={false}
      title="Edit account"
      description="Correct the account's display name or login email."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!valid || pending}
            loading={pending}
            data-testid="portal-account-edit-save"
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="portal-account-edit-dialog">
        {envelopeCount > 0 && (
          <div
            className="rounded-md border border-status-warning bg-status-warning/10 p-space-3 text-small text-onyx"
            role="alert"
            data-testid="portal-account-edit-envelope-warning"
          >
            {envelopeCount} e-signature envelope
            {envelopeCount === 1 ? " is" : "s are"} in flight for this
            account. Changing the email does not update envelopes already sent
            — recipients may need a fresh envelope.
          </div>
        )}
        <div>
          <label
            htmlFor="portal-account-edit-name"
            className="block text-small text-gray-mid"
          >
            Full name
          </label>
          <Input
            id="portal-account-edit-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            disabled={pending}
            data-testid="portal-account-edit-name"
          />
        </div>
        <div>
          <label
            htmlFor="portal-account-edit-email"
            className="block text-small text-gray-mid"
          >
            Login email
          </label>
          <Input
            id="portal-account-edit-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            data-testid="portal-account-edit-email"
          />
        </div>
        <p className="text-caption text-gray-mid">
          Changing the login email invalidates outstanding invite/reset links
          and, for unconfirmed accounts, re-sends the invitation to the
          corrected address.
        </p>
      </div>
    </Modal>
  );
}

// WS2 — set-temporary-password dialog (mirrors the in-page EditAccountDialog
// mount pattern). Explains the one-time nature, generates on demand, then
// shows the returned password once in a read-only field with copy-to-clipboard.
// The value is never toasted or logged — only rendered here.
function SetTemporaryPasswordDialog({
  pending,
  result,
  onGenerate,
  onClose,
}: {
  pending: boolean;
  result: TemporaryPasswordResult | null;
  onGenerate: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      toast.success("Temporary password copied.");
    } catch {
      toast.error("Couldn't copy — select the field and copy it manually.");
    }
  };

  return (
    <Modal
      open
      onClose={pending ? () => {} : onClose}
      dismissOnBackdropClick={false}
      title="Set temporary password"
      description="Generate a one-time password to hand to the user directly."
      size="sm"
      footer={
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          {result ? "Done" : "Cancel"}
        </Button>
      }
    >
      <div
        className="flex flex-col gap-space-3"
        data-testid="portal-account-temp-password-dialog"
      >
        {result ? (
          <>
            <div
              className="rounded-md border border-status-warning bg-status-warning/10 p-space-3 text-small text-onyx"
              role="alert"
            >
              Shown once — copy it now. This password is not emailed; share it
              with the user directly.
            </div>
            <div>
              <label
                htmlFor="portal-account-temp-password"
                className="block text-small text-gray-mid"
              >
                Temporary password
              </label>
              <div className="flex items-center gap-space-2">
                <Input
                  id="portal-account-temp-password"
                  value={result.temporaryPassword}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                  data-testid="portal-account-temp-password"
                />
                <Button type="button" variant="secondary" onClick={copy}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
            {result.mustChangeOnNextLogin && (
              <p className="text-caption text-gray-mid">
                The user will be required to change this password at next
                sign-in.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-small text-onyx">
              This sets a temporary password. Share it with the user directly —
              it will NOT be emailed. They will be required to change it at next
              sign-in. It is shown only once.
            </p>
            <div>
              <Button
                type="button"
                onClick={onGenerate}
                disabled={pending}
                loading={pending}
                data-testid="portal-account-temp-password-generate"
              >
                Generate temporary password
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// Shared age chip mirroring the queue's "Sent Nd · Wait Nd" presentation.
function AgeChip({ s }: { s: StagedSubmissionListItem }) {
  const sent = s.daysSinceSent;
  const wait = s.reviewWaitDays;
  if (sent == null && wait == null) {
    return <span className="text-caption text-gray-mid italic">—</span>;
  }
  const label = `${sent != null ? `Sent ${sent}d` : "Sent —"} · ${
    wait != null ? `Wait ${wait}d` : "Wait —"
  }`;
  return (
    <span className="text-small text-onyx" title="Sent = days since the package was sent · Wait = days awaiting review">
      <Clock size={12} className="mr-1 inline" />
      {label}
    </span>
  );
}

function SubmissionCell({ s }: { s: StagedSubmissionListItem }) {
  return (
    <div className="flex flex-col">
      <span className="font-semibold text-onyx">{s.templateName}</span>
      <span className="text-caption text-gray-mid truncate max-w-md">
        {s.originalFileName} · {formatSize(s.sizeBytes)}
      </span>
    </div>
  );
}

function CompletedTab({
  accountId,
  rows,
  loading,
  error,
}: {
  accountId: string;
  rows: StagedSubmissionListItem[];
  loading: boolean;
  error: string | null;
}) {
  const { date } = useDateFormatter();
  const columns: Column<StagedSubmissionListItem>[] = [
    { key: "file", header: "Submission", render: (s) => <SubmissionCell s={s} /> },
    {
      key: "package",
      header: "Package",
      render: (s) => (
        <Link
          to={`/collection/packages/${s.packageId}?from=${accountId}`}
          className="text-small text-onyx hover:text-gold-shadow"
        >
          #{s.packageId.slice(0, 8)}
        </Link>
      ),
    },
    {
      key: "tag",
      header: "Tag",
      render: (s) =>
        s.documentTagCode ? (
          <span className="text-small text-onyx">{s.documentTagCode}</span>
        ) : (
          <span className="text-caption text-gray-mid italic">—</span>
        ),
    },
    { key: "age", header: "Age", render: (s) => <AgeChip s={s} /> },
    {
      key: "filed",
      header: "Filed",
      // The list row carries the submission's createdAt; the staging row goes
      // terminal in place, so this is the best available filed timestamp.
      render: (s) => <span className="text-small text-onyx">{date(s.createdAt)}</span>,
    },
  ];
  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(s) => s.id}
      loading={loading}
      error={error}
      emptyMessage="No completed submissions yet."
    />
  );
}
